import type {
  ConnectingEvent,
  EndEvent,
  HoldEvent,
  IceCandidateEvent,
  IncomingEvent,
  Info,
  OutgoingEvent,
  PeerConnectionEvent,
  ReInviteEvent,
  RTCSession,
  SDPEvent,
  TerminateOptions,
} from 'jssip/lib/RTCSession';
import type { IncomingRequest } from 'jssip/lib/SIPMessage';
import type { URI } from 'jssip/lib/URI';
import { firstValueFrom, map, race, Subject, timeout } from 'rxjs';

import type {
  SipInfoMessage,
  SipSessionAcceptedEvent,
  SipMessageEvent,
} from 'features/sip/interfaces/';

export interface ReInviteEventWithSession extends ReInviteEvent {
  session: RTCSession;
}

export class SipSessionEventBus {
  public static readonly accepted$ = new Subject<SipSessionAcceptedEvent>();
  public static readonly failed$ = new Subject<EndEvent>();
  public static readonly getUserMediaFailed$ = new Subject<void>();
  public static readonly ended$ = new Subject<EndEvent>();
  public static readonly reInvite$ = new Subject<ReInviteEventWithSession>();
  public static readonly connecting$ = new Subject<ConnectingEvent>();
  public static readonly progress$ = new Subject<
    OutgoingEvent | IncomingEvent
  >();
  public static readonly sdp$ = new Subject<SDPEvent>();
  public static readonly icecandidate$ = new Subject<IceCandidateEvent>();
  public static readonly incomingDtmf$ = new Subject<IncomingRequest>();

  // Actions
  public static readonly terminate$ = new Subject<TerminateOptions | void>();
  public static readonly refer$ = new Subject<string | URI>();
  public static readonly peerConnection$ = new Subject<PeerConnectionEvent>();
  public static readonly updateCall$ = new Subject<OutgoingEvent>();
  public static readonly endCall$ = new Subject<void>();

  // Hold / unhold
  public static readonly hold$ = new Subject<HoldEvent>();
  public static readonly unhold$ = new Subject<HoldEvent>();

  // SIP INFO messages
  public static readonly incomingInfo$ = new Subject<Info>();
  public static readonly outgoingInfo$ = new Subject<SipInfoMessage>();
  // SIP MESSAGE events
  public static readonly outgoingMessage$ = new Subject<SipMessageEvent>();

  // Remote track events
  public static readonly track$ = new Subject<RTCTrackEvent>();

  // REFER result signals
  public static readonly referAccepted$ = new Subject<void>();
  public static readonly referFailed$ = new Subject<unknown>();

  /**
   * Sends a SIP REFER and returns a Promise that resolves when the
   * server responds with a 202 Accepted, or rejects on failure/timeout.
   */
  public static sendRefer(uri: string | URI, timeoutMs = 10000): Promise<void> {
    const result$ = race(
      SipSessionEventBus.referAccepted$.pipe(map(() => true)),
      SipSessionEventBus.referFailed$.pipe(
        map((err) => {
          throw err instanceof Error
            ? err
            : new Error(`SIP REFER failed: ${String(err)}`);
        })
      )
    ).pipe(timeout(timeoutMs));

    const promise = firstValueFrom(result$);
    SipSessionEventBus.refer$.next(uri);
    return promise.then(() => undefined);
  }
}
